import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { PureComponent } from 'react';
import { connect } from 'react-redux';

import { GroupRole } from '@/__generated__/graphql';
import { inviteUser } from '@/actions/invitation';
import { notify } from '@/actions/ui';
import Tooltip from '@/componentsv2/Tooltip';
import { RootState } from '@/store';
import { red500, warningAmber } from '@/theme/colors';

import FAIcon from '../Icon/FAIcon';
import Link from '../Link';
import Picture from '../Picture';
import { Select } from '../Select/Select';
import Tidbit from '../Tidbit';
import s from './GroupMember.module.scss';

const roleText: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

const inviteButtonStyle = {
  style: {
    lineHeight: '16px',
    height: '16px',
    minWidth: 0,
    fontSize: 14,
    paddingLeft: 8,
    paddingRight: 8,
    textTransform: 'capitalize' as const,
  },
};

export interface GroupMemberProps {
  groupId: string;
  member: {
    id: string;
    role: GroupRole;
    email?: string | null;
    user?: any;
    hasUpcomingConsultations?: boolean | null;
    hasActiveRequests?: boolean | null;
  };
  allowedRoles: string[];
  disabledRoles?: string[];
  removeGroupMember?: ((id: string) => void) | null;
  updateGroupMember?: ((id: string, role: GroupRole) => void) | null;
  removeDisabledReason?: string;
  roleDisabledReason?: string;
}

interface GroupMemberState {
  resendInviteAvailable: boolean;
  sendingInvite?: boolean;
}

// Connect resolves the `inviteUser` thunk down to its promise, so the props
// signatures differ from the action creators' own return types.
type Props = GroupMemberProps & {
  viewer: RootState['viewer'];
  inviteUser: (...args: Parameters<typeof inviteUser>) => Promise<any>;
  notify: (...args: Parameters<typeof notify>) => unknown;
};

class GroupMember extends PureComponent<Props, GroupMemberState> {
  state: GroupMemberState = {
    resendInviteAvailable: true,
  };

  handleRemoveClick = () => {
    const { removeGroupMember, member } = this.props;
    removeGroupMember?.(member.id);
  };

  handleRoleChange = ({ target: { value } }: { target: { value: unknown } }) => {
    const { updateGroupMember, member, allowedRoles } = this.props;
    const role = value as GroupRole;
    if (!allowedRoles.includes(role)) return;
    updateGroupMember?.(member.id, role);
  };

  handleReinvite = () => {
    const { member, inviteUser, groupId, notify } = this.props;
    this.setState({ sendingInvite: true });
    return inviteUser(
      {
        collectionType: 'group',
        collectionId: groupId,
        email: member.email,
        role: member.role,
      },
      { reinvite: true }
    ).then(
      () => {
        this.setState({ sendingInvite: false, resendInviteAvailable: false });
        notify('Invitation sent.');
      },
      () => {
        this.setState({ sendingInvite: false, resendInviteAvailable: false });
        notify('Unable to resend invite', 'error');
      }
    );
  };

  render() {
    const {
      member,
      allowedRoles,
      disabledRoles = [],
      removeGroupMember,
      updateGroupMember,
      removeDisabledReason,
      roleDisabledReason,
    } = this.props;
    const { email, user, role } = member;
    const { html_url: htmlUrl, name, city, country } = user || {};

    const { sendingInvite, resendInviteAvailable } = this.state;

    return (
      <div className={s.root}>
        <Picture user={user} size={60} />

        <div className={s.body}>
          <div className={s.header}>
            {user ? (
              <Link to={htmlUrl}>
                <h3 className={s.name}>{name}</h3>
              </Link>
            ) : (
              <div>
                <h3 className={s.name}>{email}</h3>
                <div className={s.memberInvited}>
                  Invite Sent.{' '}
                  {resendInviteAvailable && (
                    <Button
                      color="secondary"
                      disabled={sendingInvite}
                      {...inviteButtonStyle}
                      onClick={this.handleReinvite}
                    >
                      {sendingInvite ? 'Sending...' : 'Resend'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          <Tidbit city={city} country={country} />
          {member.hasUpcomingConsultations && (
            <div style={{ fontSize: 12, color: warningAmber, marginTop: 2 }}>
              <FAIcon icon="calendar-o" size={12} color={warningAmber} style={{ marginRight: 4 }} />
              Has upcoming consultations
            </div>
          )}
          {member.hasActiveRequests && (
            <div style={{ fontSize: 12, color: warningAmber, marginTop: 2 }}>
              <FAIcon icon="star-o" size={12} color={warningAmber} style={{ marginRight: 4 }} />
              Has active Expert Requests
            </div>
          )}
        </div>

        <div className={s.actions}>
          <Tooltip title={roleDisabledReason || ''} disableHoverListener={!roleDisabledReason}>
            <span>
              <Select
                id={`memberRole${email}`}
                value={role}
                disabled={!updateGroupMember || allowedRoles.length <= 1}
                style={{ width: 110 }}
                onChange={this.handleRoleChange}
                sort={false}
                margin="dense"
                options={[
                  { label: roleText[role] || role, value: role },
                  ...allowedRoles
                    .filter((r: any) => r !== role)
                    .map((r: any) => ({
                      label: roleText[r] || r,
                      value: r,
                      disabled: disabledRoles.includes(r),
                    })),
                ]}
              />
            </span>
          </Tooltip>
          <Tooltip title={removeDisabledReason || ''} disableHoverListener={!removeDisabledReason}>
            <span>
              <IconButton
                style={{ marginRight: -12, color: removeGroupMember ? red500 : undefined }}
                disabled={!removeGroupMember}
                onClick={this.handleRemoveClick}
              >
                <FAIcon icon="trash-o" color={removeGroupMember ? red500 : undefined} size={20} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
    );
  }
}

export default connect((state: RootState) => ({ viewer: state.viewer }), {
  inviteUser,
  notify,
})(GroupMember);
